import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { contentAPI } from '../api/content';
import { Image, Video, Loader2, Download, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const AIContentGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState([]);
  const [activeVideos, setActiveVideos] = useState([]);
  
  // DALL-E State
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatedImages, setGeneratedImages] = useState([]);
  
  // HeyGen State
  const [heygenAvatars, setHeygenAvatars] = useState([]);
  const [heygenVoices, setHeygenVoices] = useState([]);
  const [heygenForm, setHeygenForm] = useState({
    title: '',
    script: '',
    avatar_id: '',
    voice_id: ''
  });
  
  // Runway State
  const [runwayForm, setRunwayForm] = useState({
    title: '',
    prompt_text: '',
    duration: 5,
    model: 'gen3a_turbo'
  });
  
  useEffect(() => {
    loadHeyGenData();
    loadContentHistory();
  }, []);
  
  const loadHeyGenData = async () => {
    try {
      const [avatarsRes, voicesRes] = await Promise.all([
        contentAPI.getHeyGenAvatars(),
        contentAPI.getHeyGenVoices()
      ]);
      setHeygenAvatars(avatarsRes.data.avatars || []);
      setHeygenVoices(voicesRes.data.voices || []);
    } catch (error) {
      console.error('Error loading HeyGen data:', error);
    }
  };
  
  const loadContentHistory = async () => {
    try {
      const [imagesRes, videosRes] = await Promise.all([
        contentAPI.getImageHistory({ limit: 10 }),
        contentAPI.getVideoHistory({ limit: 10 })
      ]);
      setGeneratedImages(imagesRes.data.images || []);
      setActiveVideos(videosRes.data.videos || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };
  
  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Please enter an image prompt');
      return;
    }
    
    setLoading(true);
    try {
      const response = await contentAPI.generateImage({
        prompt: imagePrompt,
        number_of_images: 1
      });
      
      toast.success('Image generated successfully!');
      setGeneratedImages([response.data, ...generatedImages]);
      setImagePrompt('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateHeyGenVideo = async () => {
    if (!heygenForm.script.trim() || !heygenForm.avatar_id || !heygenForm.voice_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await contentAPI.generateHeyGenVideo(heygenForm);
      toast.success('HeyGen video generation started! Check status below.');
      setActiveVideos([{ _id: response.data.video_id, status: 'queued', type: 'heygen', ...heygenForm }, ...activeVideos]);
      setHeygenForm({ title: '', script: '', avatar_id: '', voice_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateRunwayVideo = async () => {
    if (!runwayForm.prompt_text.trim()) {
      toast.error('Please enter a video prompt');
      return;
    }
    
    setLoading(true);
    try {
      const response = await contentAPI.generateRunwayVideo(runwayForm);
      toast.success('Runway video generation started! Check status below.');
      setActiveVideos([{ _id: response.data.video_id, status: 'queued', type: 'runway', ...runwayForm }, ...activeVideos]);
      setRunwayForm({ title: '', prompt_text: '', duration: 5, model: 'gen3a_turbo' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
    } finally {
      setLoading(false);
    }
  };
  
  const refreshVideoStatus = async (videoId) => {
    try {
      const response = await contentAPI.getVideoStatus(videoId);
      setActiveVideos(activeVideos.map(v => 
        v._id === videoId ? { ...v, ...response.data } : v
      ));
    } catch (error) {
      console.error('Error refreshing video status:', error);
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing': return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      default: return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">AI Content Generator</h1>
          <p className="text-gray-600 mt-2">Create stunning images and videos with AI</p>
        </div>
        
        <Tabs defaultValue="images" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="images">DALL-E Images</TabsTrigger>
            <TabsTrigger value="heygen">HeyGen Videos</TabsTrigger>
            <TabsTrigger value="runway">Runway Videos</TabsTrigger>
          </TabsList>
          
          {/* DALL-E Tab */}
          <TabsContent value="images" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Generate Image with DALL-E 3
                </CardTitle>
                <CardDescription>Create high-quality images from text descriptions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Image Prompt</Label>
                  <Textarea
                    placeholder="A professional business team collaborating in a modern office..."
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button onClick={handleGenerateImage} disabled={loading} className="w-full">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</> : 'Generate Image'}
                </Button>
              </CardContent>
            </Card>
            
            {/* Generated Images Gallery */}
            {generatedImages.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Generated Images</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {generatedImages.map((img, idx) => (
                      <div key={idx} className="space-y-2">
                        <img
                          src={`data:image/png;base64,${img.image_base64}`}
                          alt={img.prompt}
                          className="w-full h-48 object-cover rounded-lg border"
                        />
                        <p className="text-xs text-gray-600 line-clamp-2">{img.prompt}</p>
                        <Button size="sm" variant="outline" className="w-full">
                          <Download className="h-4 w-4 mr-2" /> Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* HeyGen Tab */}
          <TabsContent value="heygen" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Generate AI Avatar Video with HeyGen
                </CardTitle>
                <CardDescription>Create professional videos with AI avatars</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Video Title</Label>
                  <Input
                    placeholder="My Product Demo"
                    value={heygenForm.title}
                    onChange={(e) => setHeygenForm({ ...heygenForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Script</Label>
                  <Textarea
                    placeholder="Welcome to our product demonstration..."
                    value={heygenForm.script}
                    onChange={(e) => setHeygenForm({ ...heygenForm, script: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Avatar</Label>
                    <Select value={heygenForm.avatar_id} onValueChange={(v) => setHeygenForm({ ...heygenForm, avatar_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select avatar" />
                      </SelectTrigger>
                      <SelectContent>
                        {heygenAvatars.map(avatar => (
                          <SelectItem key={avatar.avatar_id} value={avatar.avatar_id}>
                            {avatar.avatar_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Voice</Label>
                    <Select value={heygenForm.voice_id} onValueChange={(v) => setHeygenForm({ ...heygenForm, voice_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        {heygenVoices.map(voice => (
                          <SelectItem key={voice.voice_id} value={voice.voice_id}>
                            {voice.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleGenerateHeyGenVideo} disabled={loading} className="w-full">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</> : 'Generate Video'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Runway Tab */}
          <TabsContent value="runway" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Generate Video with Runway Gen-3
                </CardTitle>
                <CardDescription>Create cinematic AI videos from text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Video Title</Label>
                  <Input
                    placeholder="My Runway Video"
                    value={runwayForm.title}
                    onChange={(e) => setRunwayForm({ ...runwayForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Video Prompt</Label>
                  <Textarea
                    placeholder="A cinematic shot of a sunset over the ocean..."
                    value={runwayForm.prompt_text}
                    onChange={(e) => setRunwayForm({ ...runwayForm, prompt_text: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (seconds)</Label>
                    <Select value={runwayForm.duration.toString()} onValueChange={(v) => setRunwayForm({ ...runwayForm, duration: parseInt(v) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 seconds</SelectItem>
                        <SelectItem value="10">10 seconds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Select value={runwayForm.model} onValueChange={(v) => setRunwayForm({ ...runwayForm, model: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gen3a_turbo">Gen-3 Alpha Turbo</SelectItem>
                        <SelectItem value="gen4_turbo">Gen-4 Turbo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleGenerateRunwayVideo} disabled={loading} className="w-full">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</> : 'Generate Video'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Active Videos Status */}
        {activeVideos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Video Generation Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activeVideos.map((video) => (
                  <div key={video._id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(video.status)}
                      <div>
                        <p className="font-medium">{video.title || 'Untitled'}</p>
                        <p className="text-sm text-gray-600">{video.type?.toUpperCase()} - {video.status}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {video.status !== 'completed' && video.status !== 'failed' && (
                        <Button size="sm" variant="outline" onClick={() => refreshVideoStatus(video._id)}>
                          Refresh
                        </Button>
                      )}
                      {video.video_url && (
                        <Button size="sm" asChild>
                          <a href={video.video_url} target="_blank" rel="noopener noreferrer">
                            View Video
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AIContentGenerator;
